import { supabase } from './supabase'
import { clearStaleSupabaseAuthStorage, supabaseProjectRefFromUrl } from './supabaseProject'

/** Parse Supabase auth tokens from URL hash and query (password reset, magic link). */
export function parseAuthParamsFromUrl(searchParams: URLSearchParams) {
  const hashParams = new URLSearchParams(window.location.hash.substring(1))
  return {
    accessToken: hashParams.get('access_token'),
    refreshToken: hashParams.get('refresh_token'),
    hashType: hashParams.get('type'),
    queryToken: searchParams.get('token_hash') ?? searchParams.get('token'),
    queryType: searchParams.get('type'),
  }
}

export function isRecoveryFlow(searchParams: URLSearchParams): boolean {
  const { hashType, queryType } = parseAuthParamsFromUrl(searchParams)
  return hashType === 'recovery' || queryType === 'recovery'
}

/** Establish session from hash or verify recovery OTP from email link. */
export async function establishRecoverySession(searchParams: URLSearchParams) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
  clearStaleSupabaseAuthStorage(supabaseProjectRefFromUrl(supabaseUrl))

  const { accessToken, refreshToken, hashType, queryToken, queryType } =
    parseAuthParamsFromUrl(searchParams)

  if (accessToken && hashType === 'recovery') {
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken || '',
    })
    if (error) throw error
    if (!data.session) throw new Error('No session after recovery link')
    window.history.replaceState(null, '', window.location.pathname)
    return data.session
  }

  if (queryToken && queryType === 'recovery') {
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: queryToken,
      type: 'recovery',
    })
    if (error) throw error
    if (!data.session) throw new Error('No session after recovery verification')
    return data.session
  }

  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (session) return session

  throw new Error('Invalid or expired password reset link. Request a new email from the login page.')
}
